import styled from "@emotion/styled"
import type { Direction, TableUpdateEventDetail } from "@porsche-design-system/components-react"
import { PFlag, PHeading, PTable, PTableBody, PText } from "@porsche-design-system/components-react"
import { spacingStaticXs, spacingStaticSm, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { DealershipListItem, DealershipListView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import { FormattedMessage } from "react-intl"
import { TableRowA, TableRowWithLink, TableCellFixed } from "src/components"
import { DealershipListTableHead } from "./DealershipListTableHead"
import type { HeadSortingItem } from "./DealershipListTableHead"
import { dealershipListMessages } from "./dealershipList.messages"
import { dealershipListColumns } from "./dealershipListColumns.const"
import { getLanguageFlagName } from "./getLanguageFlagName"
import { headSortingItems } from "./headSortingItems.const"

interface DealershipListProps {
	getDealerAppUrl: (item: DealershipListItem, locale?: string) => string
	items: DealershipListView["result"]["items"]
	totalCount: DealershipListView["result"]["totalCount"]
	updateSort: (sort: Sort) => void
}

export interface Sort {
	field: string
	sortDirection: Direction | undefined
}

export const DealershipList = ({ getDealerAppUrl, items, totalCount, updateSort }: DealershipListProps) => {
	const [headItems, setHeadItems] = useState<HeadSortingItem[]>(headSortingItems)

	const onSortingChange = useCallback(
		(e: CustomEvent<TableUpdateEventDetail>) => {
			const { direction, id } = e.detail
			setHeadItems((prev) =>
				prev.map((item) => ({
					...item,
					active: false,
					...(item.id === id && e.detail),
				})),
			)
			updateSort({ field: id, sortDirection: direction })
		},
		[updateSort],
	)

	return (
		<>
			<Counter size="sm">
				<FormattedMessage {...dealershipListMessages.totalCount} values={{ count: totalCount }} />
			</Counter>
			<PTable caption="Dealership List" layout="fixed" sticky onUpdate={onSortingChange}>
				<DealershipListTableHead items={headItems} />

				<PTableBody>
					{items.map((item) => (
						<TableRowWithLink key={item.entryDn}>
							<TableCellFixed $width={dealershipListColumns.displayName}>
								<TableRowA href={getDealerAppUrl(item)} target="_blank" rel="noopener noreferrer">
									{item.displayName}
								</TableRowA>
							</TableCellFixed>
							<TableCellFixed $width={dealershipListColumns.partnerNumber}>
								{item.partnerNumber}
							</TableCellFixed>
							<TableCellFixed $width={dealershipListColumns.organisationFunction}>
								{item.organisationFunction}
							</TableCellFixed>
							<TableCellFixed $width={dealershipListColumns.country}>{item.country}</TableCellFixed>
							<TableCellFixed $width={dealershipListColumns.chooseLanguage}>
								<LanguageLinks>
									{item.languageTags?.map((locale) => (
										<LanguageLink key={locale} href={getDealerAppUrl(item, locale)} target="_blank">
											<PFlag name={getLanguageFlagName(locale)} />
											{locale.slice(0, 2)}
										</LanguageLink>
									))}
								</LanguageLinks>
							</TableCellFixed>
						</TableRowWithLink>
					))}
				</PTableBody>
			</PTable>
			{!totalCount && (
				<NoResults align="center">
					<FormattedMessage {...dealershipListMessages.noResultsHeadline} />
				</NoResults>
			)}
		</>
	)
}

const Counter = styled(PHeading)`
	margin-bottom: ${spacingStaticSm};
`

const NoResults = styled(PText)`
	margin: ${spacingStaticMd} 0 0;
`

const LanguageLinks = styled.div`
	display: flex;
	gap: ${spacingStaticMd};
`

const LanguageLink = styled.a`
	position: relative;
	display: inline-flex;
	gap: ${spacingStaticXs};
	padding-inline: 0 ${spacingStaticSm};
	border-radius: calc(infinity * 1px);
`
